import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ShieldAlert, CheckCircle2, Loader2 } from 'lucide-react';
import SEOHead, { generateBreadcrumbSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LegalDMCA() {
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    complainant_name: '',
    complainant_email: '',
    complainant_phone: '',
    complainant_address: '',
    complainant_company: '',
    complainant_role: '',
    work_description: '',
    work_url: '',
    infringing_url: '',
    infringing_description: '',
    good_faith_belief: false,
    accuracy_perjury: false,
    signature: '',
    _hp_company: '',
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.good_faith_belief || !form.accuracy_perjury) {
      toast.error('Please confirm both statutory affirmations to file a valid DMCA notice.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.work_url) delete payload.work_url;
      const res = await axios.post(`${API_URL}/api/dmca/notice`, payload);
      setSubmitted(res.data?.notice_id || 'received');
      toast.success('DMCA notice received');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit notice. Email dmca@theailedger.ai instead.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white px-6 py-16">
        <div className="max-w-2xl mx-auto bg-neutral-900/60 border border-white/10 rounded-2xl p-8 text-center">
          <CheckCircle2 className="w-14 h-14 text-[#00F0FF] mx-auto mb-5" />
          <h1 className="text-2xl font-bold mb-3">DMCA notice received</h1>
          <p className="text-neutral-400 mb-4">
            Reference ID: <span className="font-mono text-white">{submitted}</span>
          </p>
          <p className="text-sm text-neutral-400 leading-relaxed">
            Our team will triage within 48 hours. You&apos;ll get a confirmation email at the
            address you provided, and a follow-up once we take action on the URL you reported.
          </p>
          <Link to="/" className="inline-block mt-6 text-[#00F0FF] hover:underline">
            ← Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dmca-page">
      <SEOHead
        title="DMCA Takedown Policy — theailedger.ai"
        description="Submit a DMCA copyright takedown notice. theailedger.ai removes infringing material expeditiously under 17 USC §512(c)."
        path="/legal/dmca"
        keywords="theailedger DMCA, copyright takedown, 512 safe harbor, designated agent, theailedger.ai copyright"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'DMCA Takedown Policy', path: '/legal/dmca' },
            ]),
          ],
        }}
      />

      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-[#00F0FF] font-bold tracking-tight">theailedger.ai</Link>
          <Link to="/" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-12 pb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 text-xs font-semibold mb-5">
          <ShieldAlert className="w-3.5 h-3.5" />
          DMCA · 17 USC §512
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Copyright takedown policy</h1>
        <div className="prose prose-invert max-w-none text-neutral-300 text-sm leading-relaxed space-y-4">
          <p>
            theailedger.ai respects intellectual property rights. If you believe content available
            through theailedger.ai infringes your copyright, you may file a takedown notice under
            the Digital Millennium Copyright Act using the form below.
          </p>
          <p>
            <strong>Designated agent:</strong> dmca@theailedger.ai · theailedger LLC, Washington, USA.
            Our designated agent is also registered with the U.S. Copyright Office.
          </p>
          <p>
            A valid DMCA notice must contain the elements specified in 17 USC §512(c)(3) —
            identification of the work, identification of the infringing material, contact
            information, a good-faith statement, an accuracy statement under penalty of perjury,
            and the rights holder&apos;s signature. The form below collects these.
          </p>
          <p>
            <strong>Counter-notice:</strong> if you believe your content was removed in error, you
            may file a counter-notice under 17 USC §512(g). Email dmca@theailedger.ai. We will
            restore the material 10–14 business days after receipt unless we receive notice that
            the original complainant has filed a court action.
          </p>
          <p>
            <strong>Repeat infringers:</strong> theailedger.ai terminates accounts of users
            determined to be repeat copyright infringers, consistent with §512(i).
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20">
        <form onSubmit={submit} className="bg-neutral-900/40 border border-white/10 rounded-2xl p-5 sm:p-6 space-y-5">
          <h2 className="text-lg font-bold">Submit a DMCA notice</h2>

          {/* Honeypot */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 0, height: 0, overflow: 'hidden' }}>
            <label htmlFor="dmca_company">Company (leave blank)</label>
            <input id="dmca_company" name="company" tabIndex={-1} autoComplete="off"
                   value={form._hp_company} onChange={update('_hp_company')} />
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-bold text-[#00F0FF] uppercase tracking-wide mb-2">Your information</legend>
            <Field label="Full legal name *" testid="dmca-name">
              <input required value={form.complainant_name} onChange={update('complainant_name')}
                     className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Email *" testid="dmca-email">
                <input required type="email" value={form.complainant_email} onChange={update('complainant_email')}
                       className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
              </Field>
              <Field label="Phone (optional)">
                <input value={form.complainant_phone} onChange={update('complainant_phone')}
                       className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
              </Field>
            </div>
            <Field label="Postal address *" testid="dmca-address">
              <textarea required rows={2} value={form.complainant_address} onChange={update('complainant_address')}
                        className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] resize-none" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Company (optional)">
                <input value={form.complainant_company} onChange={update('complainant_company')}
                       className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
              </Field>
              <Field label="Acting as (e.g. 'Counsel for Acme Inc.')">
                <input value={form.complainant_role} onChange={update('complainant_role')}
                       className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
              </Field>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-bold text-[#00F0FF] uppercase tracking-wide mb-2">The work being infringed</legend>
            <Field label="Description of the copyrighted work *" testid="dmca-work-desc">
              <textarea required rows={3} value={form.work_description} onChange={update('work_description')}
                        placeholder="e.g. 'A photograph titled \\'Sunrise over Boulder\\' first published on 2023-08-12 on my website.'"
                        className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] resize-none" />
            </Field>
            <Field label="URL of the original work (optional)">
              <input type="url" value={form.work_url} onChange={update('work_url')}
                     placeholder="https://your-site.com/original"
                     className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
            </Field>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-bold text-[#00F0FF] uppercase tracking-wide mb-2">The allegedly infringing material on theailedger.ai</legend>
            <Field label="Specific theailedger.ai URL *" testid="dmca-infringing-url">
              <input required type="url" value={form.infringing_url} onChange={update('infringing_url')}
                     placeholder="https://theailedger.ai/featured/..."
                     className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
            </Field>
            <Field label="Description of the infringing material *" testid="dmca-infringing-desc">
              <textarea required rows={3} value={form.infringing_description} onChange={update('infringing_description')}
                        placeholder="e.g. 'The product image at the top of the page is my photograph used without permission.'"
                        className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] resize-none" />
            </Field>
          </fieldset>

          <fieldset className="space-y-3 pt-2 border-t border-white/10">
            <legend className="text-sm font-bold text-[#00F0FF] uppercase tracking-wide mb-2">Statutory affirmations</legend>
            <label className="flex items-start gap-3 text-sm text-neutral-300">
              <input required type="checkbox" checked={form.good_faith_belief} onChange={update('good_faith_belief')}
                     data-testid="dmca-good-faith"
                     className="mt-1 accent-[#00F0FF]" />
              <span>I have a good-faith belief that the use of the material identified above is not authorized by the copyright owner, its agent, or the law.</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-neutral-300">
              <input required type="checkbox" checked={form.accuracy_perjury} onChange={update('accuracy_perjury')}
                     data-testid="dmca-perjury"
                     className="mt-1 accent-[#00F0FF]" />
              <span>I swear under penalty of perjury that the information in this notice is accurate and that I am the copyright owner or am authorized to act on the owner&apos;s behalf.</span>
            </label>
            <Field label="Electronic signature (type your full legal name) *" testid="dmca-signature">
              <input required value={form.signature} onChange={update('signature')}
                     className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]" />
            </Field>
          </fieldset>

          <button type="submit" disabled={submitting} data-testid="dmca-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 transition disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitting ? 'Submitting…' : 'Submit DMCA notice'}
          </button>
          <p className="text-xs text-neutral-500 leading-relaxed">
            Submitting false information may expose you to liability under 17 USC §512(f). If
            you&apos;re unsure whether use qualifies as fair use, please consult an attorney before filing.
          </p>
        </form>
      </section>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-1.5">{label}</span>
      <span data-testid={testid}>{children}</span>
    </label>
  );
}
